import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import { Alert, Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';

import WorkspaceInput from '@components/Common/WorkspaceInput';

interface Turn {
  role: 'user' | 'assistant';
  content: string;
  /** The body as it stood BEFORE this turn, so any single edit can be undone. */
  previousHtml?: string;
  lostContent?: boolean;
  wordsBefore?: number;
  wordsAfter?: number;
}

interface RevisionChatProps {
  html: string;
  onRevised: (html: string) => void;
}

/** A few openers, because a blank box invites nothing and the vocabulary is not obvious. */
const SUGGESTIONS = [
  'Turn the salary figures into a bar chart',
  'Put the key takeaways in a tinted band',
  'Make the comparison a two-column card pair',
  'Add a callout warning before the negotiation section',
];

/**
 * Editing the guide by asking, rather than by regenerating it.
 *
 * Regenerating a whole guide to move one section costs several minutes and a full generation, and
 * it discards every hand edit made since the import. This sends the body that exists plus one
 * instruction, and puts the result in the editor.
 *
 * **Undo is the feature, not a nicety.** Each turn keeps the body as it stood before it, so a
 * revision that went wrong is one click from being reversed, and nothing is written to the database
 * until the operator saves. That is what makes it safe to try an instruction and see.
 *
 * The warning about lost content is the other half. A model asked to change one section can reword
 * three paragraphs on its way past, and nobody scrolling a thousand-word body in a code editor will
 * notice. The server compares the visible word count before and after and says so; this shows it
 * next to the undo button, which is the only moment the operator can act on it cheaply.
 */
const RevisionChat: React.FC<RevisionChatProps> = ({ html, onRevised }) => {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  const scroller = useRef<HTMLDivElement | null>(null);

  const send = async (instruction: string) => {
    const text = instruction.trim();
    if (!text || busy) return;

    setBusy(true);
    setElapsed(0);
    setError('');
    setDraft('');
    const before = html;
    setTurns((t) => [...t, { role: 'user', content: text }]);

    try {
      /*
       * Read the stream rather than awaiting a response.
       *
       * The route streams its stages, because an edit re-emits the whole body and takes about as
       * long as generating it: the first version of this was a plain POST and came back 504 from
       * the proxy while the model was still writing. The elapsed count comes from the heartbeat, so
       * the number on screen is real rather than a spinner pretending.
       */
      const res = await fetch('/api/guides/revise', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          html: before,
          instruction: text,
          // The body itself is the context that matters, so this is only enough for "make it
          // shorter" to know what "it" was.
          history: turns.map((t) => ({ role: t.role, content: t.content })),
        }),
      });
      if (!res.ok || !res.body) {
        const failed = await res.json().catch(() => null);
        throw new Error(failed?.error || 'Could not edit the guide');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let data: any = null;
      let streamError = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const evt = JSON.parse(line);
          if (evt.stage === 'done') data = evt.revision;
          else if (evt.stage === 'error') streamError = evt.error;
          else if (evt.stage === 'working') setElapsed(evt.seconds ?? 0);
        }
      }
      if (streamError) throw new Error(streamError);
      if (!data?.html) throw new Error('The edit returned nothing to apply');

      onRevised(data.html);
      setTurns((t) => [
        ...t,
        {
          role: 'assistant',
          content: data.note || 'Updated the guide.',
          previousHtml: before,
          lostContent: !!data.lostContent,
          wordsBefore: data.wordsBefore,
          wordsAfter: data.wordsAfter,
        },
      ]);
      if ((data.problems ?? []).length > 0) {
        setError(`The edit still breaks a house rule: ${data.problems[0]}`);
      }
    } catch (e: any) {
      // The request is a fetch, so the reason arrives as e.message (the route's own sentence, such
      // as the monthly budget refusal). Reading only an axios-shaped error dropped every one of them.
      setError(e?.response?.data?.error || e?.message || 'Could not edit the guide');
      setTurns((t) => t.slice(0, -1));
    } finally {
      setBusy(false);
      requestAnimationFrame(() => {
        if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
      });
    }
  };

  const undo = (turn: Turn, index: number) => {
    if (turn.previousHtml === undefined) return;
    onRevised(turn.previousHtml);
    // The turn stays in the log, marked, rather than disappearing: what was tried is part of the
    // record, and a chat that erases its own history is one you cannot reason about.
    setTurns((t) =>
      t.map((entry, i) =>
        i === index ? { ...entry, content: `${entry.content} (undone)`, previousHtml: undefined } : entry,
      ),
    );
  };

  return (
    <Box className="ge-chat">
      <Box className="ge-chat__log" ref={scroller}>
        {turns.length === 0 && (
          <Box className="ge-chat__empty">
            <Typography variant="body2" sx={{ mb: 1 }}>
              Ask for a change and it is applied to the body on the left. Nothing is saved until you
              save, and every edit can be undone.
            </Typography>
            {SUGGESTIONS.map((s) => (
              <Button
                key={s}
                size="small"
                variant="outlined"
                sx={{ mr: 0.75, mb: 0.75 }}
                onClick={() => send(s)}
              >
                {s}
              </Button>
            ))}
          </Box>
        )}

        {turns.map((turn, i) => (
          <Box
            key={i}
            className={`ge-chat__turn ge-chat__turn--${turn.role}`}
          >
            <Typography variant="body2">{turn.content}</Typography>
            {turn.role === 'assistant' && (
              <Box className="ge-chat__meta">
                {turn.lostContent && (
                  <Typography variant="caption" className="ge-chat__warning">
                    This edit dropped text: {turn.wordsBefore} words became {turn.wordsAfter}. Check
                    what went before keeping it.
                  </Typography>
                )}
                {turn.previousHtml !== undefined && (
                  <IconButton
                    size="small"
                    aria-label="Undo this edit"
                    title="Undo this edit"
                    onClick={() => undo(turn, i)}
                  >
                    <UndoOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        ))}

        {busy && (
          <Box className="ge-chat__busy">
            <CircularProgress size={16} />
            <Typography variant="caption">
              {/* A real number from the heartbeat: an edit rewrites the whole body, so this is
                  minutes rather than seconds and a silent spinner would read as a hang. */}
              Editing the guide{elapsed > 0 ? ` (${elapsed}s)` : ''}...
            </Typography>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box className="ge-chat__composer">
        <WorkspaceInput
          value={draft}
          placeholder="Ask for a change to this guide"
          disabled={busy}
          onChange={setDraft}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || (e as React.KeyboardEvent).shiftKey) return;
            e.preventDefault();
            send(draft);
          }}
        />
        <IconButton
          aria-label="Send"
          color="primary"
          disabled={busy || !draft.trim()}
          onClick={() => send(draft)}
        >
          <SendOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default RevisionChat;
